(() => {
  if (customElements.get('renly-support-form')) return;
  let turnstileReady;
  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!turnstileReady) turnstileReady = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      const failed = () => {
        clearTimeout(timer);
        script.remove();
        reject(new Error('verification_unavailable'));
      };
      const timer = setTimeout(failed, 20000);
      script.onload = () => {
        clearTimeout(timer);
        if (window.turnstile) resolve(window.turnstile);
        else failed();
      };
      script.onerror = failed;
      document.head.appendChild(script);
    }).catch(error => {
      turnstileReady = undefined;
      throw error;
    });
    return turnstileReady;
  }
  class RenlySupportForm extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('form');
      this.button = this.form.querySelector('button[type="submit"]');
      this.status = this.querySelector('[data-status]');
      this.form.addEventListener('submit', event => this.submit(event));
      this.verificationStatus = this.querySelector('[data-verification-status]');
      this.retryButton = this.querySelector('[data-verification-retry]');
      this.retryButton.addEventListener('click', () => this.startVerification());
      this.startVerification();
    }
    disconnectedCallback() {
      clearTimeout(this.verificationTimer);
    }
    verificationFailed() {
      clearTimeout(this.verificationTimer);
      this.token = '';
      this.button.disabled = true;
      this.verificationStatus.textContent = this.dataset.verificationFailed;
      this.retryButton.hidden = false;
      this.retryButton.disabled = !!this.sending;
      return true;
    }
    async startVerification() {
      this.token = '';
      this.button.disabled = true;
      this.retryButton.hidden = true;
      this.verificationStatus.textContent = this.dataset.verificationWaiting;
      clearTimeout(this.verificationTimer);
      // Some blocked/privacy-restricted browsers never call an error callback.
      this.verificationTimer = setTimeout(() => this.verificationFailed(), 30000);
      try {
        const turnstile = await loadTurnstile();
        if (!this.isConnected) return;
        this.turnstile = turnstile;
        if (this.widget !== undefined) {
          turnstile.reset(this.widget);
          return;
        }
        this.widget = turnstile.render(this.querySelector('[data-turnstile]'), {
          sitekey: this.dataset.sitekey, action: 'support_contact', language: this.dataset.language,
          appearance: 'always',
          callback: token => {
            clearTimeout(this.verificationTimer);
            this.token = token;
            this.button.disabled = !!this.sending;
            this.verificationStatus.textContent = '';
            this.retryButton.hidden = true;
          },
          'expired-callback': () => this.startVerification(),
          'error-callback': () => this.verificationFailed(),
          'timeout-callback': () => this.verificationFailed(),
          'unsupported-callback': () => this.verificationFailed(),
        });
      } catch {
        this.verificationFailed();
      }
    }
    async submit(event) {
      event.preventDefault();
      if (this.sending || !this.form.reportValidity()) return;
      if (!this.token) { this.status.textContent = this.dataset.verification; return; }
      const fields = Object.fromEntries(new FormData(this.form));
      const payload = Object.fromEntries(['name', 'email', 'subject', 'order_number', 'message', 'website'].map(key => [key, String(fields[key] || '').trim()]));
      payload.name = payload.name || payload.email;
      payload.subject = this.dataset.subject;
      if (fields.phone) payload.message += `\n\n${this.dataset.phone}: ${String(fields.phone).trim()}`;
      const fingerprint = JSON.stringify(payload);
      if (fingerprint !== this.fingerprint) {
        this.fingerprint = fingerprint;
        this.submissionId = crypto.randomUUID();
      }
      this.sending = true;
      this.button.disabled = true;
      this.retryButton.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.waiting;
      try {
        const response = await fetch(this.dataset.endpoint, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'omit',
          body: JSON.stringify({ ...payload, submission_id: this.submissionId, turnstile_token: this.token }),
          signal: AbortSignal.timeout(30000),
        });
        const result = await response.json();
        if (!response.ok || result.ok !== true) throw new Error(result.error || 'delivery_failed');
        this.form.reset();
        this.fingerprint = null;
        this.status.textContent = this.dataset.success;
      } catch (error) {
        this.status.textContent = error.message === 'verification_failed' ? this.dataset.verification : this.dataset.error;
      } finally {
        this.sending = false;
        this.form.removeAttribute('aria-busy');
        this.retryButton.disabled = false;
        this.startVerification();
        this.status.focus();
      }
    }
  }
  customElements.define('renly-support-form', RenlySupportForm);
})();
